import React, { useState, useMemo } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiEye, FiUser } from 'react-icons/fi';
import Swal from 'sweetalert2';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import { AddAdmin, EditAdmin, AdminDetails } from '../components/admin';
import { useAdmins, useDeleteAdmin } from '../hooks/useAdmins';

const Admins = ({ user }) => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  // Fetch admins using React Query
  const {
    data: adminsResponse,
    isLoading,
    error,
    isFetching,
  } = useAdmins(pagination.current, pagination.pageSize);

  // Mutations
  const deleteAdminMutation = useDeleteAdmin();

  // Memoized admins data
  const { adminsData, totalRecords } = useMemo(() => {
    if (!adminsResponse) {
      return { adminsData: [], totalRecords: 0 };
    }

    // Ensure we always return an array for adminsData
    const admins = adminsResponse.admins.data.data || adminsResponse.data || [];
    const total = adminsResponse.admins.data.total || adminsResponse.count || 0;

    return {
      adminsData: Array.isArray(admins) ? admins : [],
      totalRecords: total
    };
  }, [adminsResponse]);

  // Check if delete mutation is loading
  const isMutating = deleteAdminMutation.isPending;

  // Get error message from mutations or query
  const errorMessage = error?.message || 
                      deleteAdminMutation.error?.message;

  // Table columns configuration
  const columns = [
    {
      key: 'id',
      title: 'ID',
      sortable: true,
    },
    {
      key: 'name',
      title: 'Name',
      sortable: true,
    },
    {
      key: 'email',
      title: 'Email',
      sortable: true,
    },
    {
      key: 'image',
      title: 'image',
      render: (value, row) => (
        <div className="flex items-center">
          {value ? (
            <img 
              src={row.image} 
              alt={row.name} 
              className="h-8 w-8 rounded-full object-cover"
            />
          ) : (
            <div className="h-8 w-8 bg-gray-300 rounded-full flex items-center justify-center">
              <FiUser className="h-4 w-4 text-gray-600" />
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, row) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleViewDetails(row)}
            className="text-blue-600 hover:text-blue-800 p-1"
            title="View Details"
            disabled={isMutating}
          >
            <FiEye className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleEditAdmin(row)}
            className="text-green-600 hover:text-green-800 p-1"
            title="Edit Admin"
            disabled={isMutating}
          >
            <FiEdit className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDeleteAdmin(row)}
            className="text-red-600 hover:text-red-800 p-1"
            title="Delete Admin"
            disabled={isMutating}
          >
            <FiTrash2 className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  // Breadcrumbs
  const breadcrumbs = [
    { label: 'Dashboard', href: '/dashboard' },
    { label: 'Admins', href: '/dashboard/admins' },
  ];

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, current: page }));
  };

  const handlePageSizeChange = (pageSize, current) => {
    setPagination(prev => ({ ...prev, pageSize, current }));
  };

  const handleAddAdmin = () => {
    setSelectedAdmin(null);
    setShowAddModal(true);
  };

  const handleEditAdmin = (admin) => {
    setSelectedAdmin(admin);
    setShowEditModal(true);
  };

  const handleViewDetails = async (admin) => {
    try {
      setSelectedAdmin(admin);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error viewing admin details:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to load admin details',
        confirmButtonText: 'OK',
        background: '#ffffff',
        color: '#333',
        customClass: {
          popup: 'rounded-lg shadow-xl',
          confirmButton: 'bg-[#dc2626] text-white font-bold py-2 px-4 rounded',
          cancelButton: 'bg-[#6b7280] text-white font-bold py-2 px-4 rounded',
        }
      });
    }
  };

  const handleDeleteAdmin = async (admin) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete admin "${admin.name}". This action cannot be undone!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      background: '#ffffff',
      color: '#333',
      customClass: {
        popup: 'rounded-lg shadow-xl',
        confirmButton: 'bg-[#dc2626] text-white font-bold py-2 px-4 rounded',
        cancelButton: 'bg-[#6b7280] text-white font-bold py-2 px-4 rounded',
      }
    });

    if (result.isConfirmed) {
      try {
        await deleteAdminMutation.mutateAsync(admin.id);
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Admin has been deleted successfully.',
          timer: 2000,
          showConfirmButton: false,
          background: '#ffffff',
          color: '#333',
          customClass: {
            popup: 'rounded-lg shadow-xl',
          }
        });
      } catch (error) {
        console.error('Error deleting admin:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete admin',
          confirmButtonText: 'OK',
        });
      }
    }
  };

  // Handle loading state with fallback data
  if (isLoading) {
    return (
      <Layout user={user}>
        <div className="space-y-0">
          <PageHeader
            title="Admins"
            description="Manage system administrators"
            breadcrumbs={breadcrumbs}
          />
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
            <span className="ml-2 text-gray-600">Loading admins...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-0">
        <PageHeader
          title="Admins"
          description="Manage system administrators"
          breadcrumbs={breadcrumbs}
        />

        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="text-red-800">{errorMessage}</div>
          </div>
        )}

        {/* Add Admin Button - Now positioned above the table */}
        <div className="flex justify-between items-center !mb-4">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Admin Management</h2>
            <p className="text-sm text-gray-600">Add, edit, or remove administrators</p>
          </div>
          <button
            onClick={handleAddAdmin}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            disabled={isMutating}
          >
            <FiPlus className="h-4 w-4" />
            <span>Add Admin</span>
          </button>
        </div>

        <DataTable
          data={adminsData}
          columns={columns}
          filterable={false}
          loading={isLoading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: totalRecords,
          }}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          emptyMessage="No admins found"
        />

        {/* Admin Modals */}
        <AddAdmin
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
        />

        <EditAdmin
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          selectedAdmin={selectedAdmin}
        />

        <AdminDetails
          isOpen={showDetailsModal}
          onClose={() => setShowDetailsModal(false)}
          selectedAdmin={selectedAdmin}
        />
      </div>
    </Layout>
  );
};

export default Admins; 